import React, { useState, useEffect } from 'react';

/**
 * Scroll To Top Button Component
 * 
 * Features:
 * - Auto-shows after scrolling down 300px
 * - Smooth scroll animation
 * - Responsive positioning (adjusts for chat sidebar)
 * - Dark mode support
 */
export default function ScrollToTopButton({ darkMode, chatMode, isChatSidebarCollapsed }) {
  const [isVisible, setIsVisible] = useState(false);

  // Show button when page is scrolled down
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (!isVisible) return null;

  // Adjust position based on chat sidebar state
  const shouldAdjustForChat = chatMode === 'sidebar' && !isChatSidebarCollapsed;

  return (
    <button
      onClick={scrollToTop}
      className={`
        fixed bottom-6 z-40
        p-3 rounded-full shadow-lg transition-all duration-300
        ${shouldAdjustForChat ? 'right-[22rem]' : 'right-6'}
        ${darkMode 
          ? 'bg-white text-gray-900 hover:bg-gray-100 border border-gray-300' 
          : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 border border-purple-700'
        }
        hover:shadow-xl hover:scale-105
        opacity-90 hover:opacity-100
      `}
      aria-label="Scroll to top"
    >
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 10l7-7m0 0l7 7m-7-7v18"
        />
      </svg>
    </button>
  );
}
